import { useMemo } from "react";
import { Clock, Layers, CalendarRange, TrendingUp } from "lucide-react";
import { fmtHours, fmtMoney } from "@/lib/calc";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function Bar({ label, sub, count, max, accent = "amber" }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  const color = accent === "emerald" ? "#10b981" : "#f59e0b";
  return (
    <div data-testid="frequent-bar-row" className="group">
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-mono-data text-sm text-[#f3f4f8] truncate">{label}</span>
        <span className="font-mono-data text-xs text-[#9fa6b9] shrink-0 ml-2">{sub}</span>
      </div>
      <div className="h-2.5 rounded-full bg-[#14161c] border border-[#2b2f3d] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color, boxShadow: `0 0 12px ${color}55` }}
        />
      </div>
    </div>
  );
}

function Panel({ title, Icon, children, testid }) {
  return (
    <div data-testid={testid} className="bg-[#181a22] border border-[#2b2f3d] rounded-xl p-5">
      <div className="flex items-center gap-2 mb-5">
        <Icon className="w-4 h-4 text-amber-500" />
        <h3 className="font-display text-lg font-bold uppercase tracking-tight text-[#f3f4f8]">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export default function FrequentInsights({ entries, currency }) {
  const data = useMemo(() => {
    const slots = {};
    const projects = {};
    const weekdays = Array.from({ length: 7 }, () => ({ count: 0, hours: 0 }));

    for (const e of entries) {
      const slotKey = `${e.start_time}–${e.end_time}`;
      if (!slots[slotKey]) slots[slotKey] = { count: 0, hours: 0 };
      slots[slotKey].count += 1;
      slots[slotKey].hours += e.duration_hours;

      if (!projects[e.project]) projects[e.project] = { count: 0, hours: 0, earnings: 0 };
      projects[e.project].count += 1;
      projects[e.project].hours += e.duration_hours;
      projects[e.project].earnings += e.earnings;

      const d = new Date(e.date + "T00:00:00");
      const idx = (d.getDay() + 6) % 7; // Monday = 0
      weekdays[idx].count += 1;
      weekdays[idx].hours += e.duration_hours;
    }

    const slotList = Object.entries(slots)
      .map(([k, v]) => ({ key: k, ...v }))
      .sort((a, b) => b.count - a.count || b.hours - a.hours)
      .slice(0, 6);

    const projectList = Object.entries(projects)
      .map(([k, v]) => ({ key: k, ...v }))
      .sort((a, b) => b.count - a.count);

    return { slotList, projectList, weekdays };
  }, [entries]);

  if (entries.length === 0) {
    return (
      <div data-testid="frequent-empty" className="bg-[#181a22] border border-dashed border-[#2b2f3d] rounded-xl p-12 text-center">
        <TrendingUp className="w-8 h-8 text-[#62697e] mx-auto" />
        <p className="mt-3 text-[#9fa6b9]">No data yet.</p>
        <p className="mt-1 font-mono-data text-xs text-[#62697e]">Log a few overtime entries to see your frequent patterns.</p>
      </div>
    );
  }

  const maxSlot = Math.max(...data.slotList.map((s) => s.count), 1);
  const maxProject = Math.max(...data.projectList.map((p) => p.count), 1);
  const maxWeekday = Math.max(...data.weekdays.map((w) => w.count), 1);

  return (
    <div data-testid="frequent-insights" className="space-y-5 fade-up">
      <Panel title="Most Worked Time Slots" Icon={Clock} testid="frequent-slots-panel">
        <div className="space-y-4">
          {data.slotList.map((s) => (
            <Bar
              key={s.key}
              label={s.key}
              sub={`${s.count}× · ${fmtHours(s.hours)}`}
              count={s.count}
              max={maxSlot}
              accent="amber"
            />
          ))}
        </div>
      </Panel>

      <Panel title="By Project / Category" Icon={Layers} testid="frequent-projects-panel">
        <div className="space-y-4">
          {data.projectList.map((p) => (
            <Bar
              key={p.key}
              label={p.key}
              sub={`${p.count}× · ${fmtMoney(p.earnings, currency)}`}
              count={p.count}
              max={maxProject}
              accent="emerald"
            />
          ))}
        </div>
      </Panel>

      <Panel title="By Day of Week" Icon={CalendarRange} testid="frequent-weekday-panel">
        <div className="grid grid-cols-7 gap-2">
          {data.weekdays.map((w, i) => {
            const pct = maxWeekday > 0 ? Math.round((w.count / maxWeekday) * 100) : 0;
            return (
              <div key={WEEKDAYS[i]} data-testid="frequent-weekday-col" className="flex flex-col items-center gap-2">
                <div className="relative w-full h-28 bg-[#14161c] border border-[#2b2f3d] rounded-lg flex items-end overflow-hidden">
                  <div
                    className="w-full rounded-t-md transition-all duration-500"
                    style={{ height: `${pct}%`, backgroundColor: "#f59e0b", boxShadow: "0 0 12px #f59e0b55" }}
                  />
                </div>
                <span className="font-mono-data text-[10px] text-[#9fa6b9] uppercase">{WEEKDAYS[i]}</span>
                <span className="font-mono-data text-xs font-bold text-amber-500">{w.count}</span>
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}
